import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "../../components/ReportSelect";
import {
  Container,
  Table,
  Button,
  Row,
  Col,
  Spinner,
  Form,
  Modal,
} from "react-bootstrap";
import Pagination from "../../components/Pagination";
import "../../styles/Reports.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;

const DOC_STATUS_OPTIONS = [
  { value: "not_completed", label: "Not Started" },
  { value: "review", label: "Ready for Review" },
  { value: "almost_expired", label: "Almost Expired" },
  { value: "completed", label: "Complete" },
];

export default function DocumentStatusReport() {
  const navigate = useNavigate();

  // -------------------------
  // Dropdown data
  // -------------------------
  const [orgUnits, setOrgUnits] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCats, setSubCats] = useState([]);
  const [groups, setGroups] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [docTypes, setDocTypes] = useState([]);

  // -------------------------
  // Table state
  // -------------------------
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [taskDetails, setTaskDetails] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  // -------------------------
  // UI filter state
  // -------------------------
  const [pOrgUnit, setPOrgUnit] = useState(null);
  const [pRecruiter, setPRecruiter] = useState(null);
  const [pCategory, setPCategory] = useState(null);
  const [pSubCat, setPSubCat] = useState(null);
  const [pGroup, setPGroup] = useState(null);
  const [pEmpStatus, setPEmpStatus] = useState(null);
  const [pDocStatus, setPDocStatus] = useState(null);
  const [pDocTypes, setPDocTypes] = useState([]);
  const [pSearch, setPSearch] = useState("");

  // Applied filters (sent to backend)
  const [filters, setFilters] = useState({});

  // -------------------------
  // Load dropdowns
  // -------------------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
      axios.get(`${BASE_URL}/api/dropdowns/recruiters`, { headers }),
      axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
      axios.get(`${BASE_URL}/api/dropdowns/sub-categories`, { headers }),
      axios.get(`${BASE_URL}/api/dropdowns/position-groups`, { headers }),
      axios.get(`${BASE_URL}/api/dropdowns/user-status`, { headers }),

      // ✅ ALL document tasks (from nwt_usertasks.ut_taskname)
      axios.get(`${BASE_URL}/api/dropdowns/document-types`, { headers }),
    ]).then(([ou, rec, cat, sub, grp, st, docs]) => {
      setOrgUnits(ou.data || []);
      setRecruiters(rec.data || []);
      setCategories(cat.data || []);
      setSubCats(sub.data || []);
      setGroups(
        grp.data.map((g) => ({
          value: g.pg_id,
          label: g.pg_name,
        }))
      );
      setStatuses(
        st.data.map((s) => ({
          value: s.s_id,
          label: s.s_TranslatedText,
        }))
      );

      // ✅ All ~32 document tasks
      setDocTypes(
        docs.data.map((d) => ({
          value: d,
          label: d,
        }))
      );
    });
  }, []);

  // -------------------------
  // Fetch report rows
  // -------------------------
  const fetchRows = useCallback(async () => {
    if (!hasSearched) return;

    setLoading(true);
    setCurrentPage(1);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const res = await axios.get(`${BASE_URL}/api/reports/document-status`, {
        headers,
        params: filters,
      });
      setRows(res.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters, hasSearched]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // -------------------------
  // Apply filters
  // -------------------------
  const applyFilters = () => {
    setHasSearched(true);
    setFilters({
      orgUnit: pOrgUnit?.value || "",
      recruiter: pRecruiter?.value || "",
      category: pCategory?.value || "",
      subCategory: pSubCat?.value || "",
      group: pGroup?.value || "",
      empStatus: pEmpStatus?.value || "",
      docStatus: pDocStatus?.value || "",
      docTypes: pDocTypes.map((d) => d.value).join(","),
      search: pSearch.trim(),
    });
  };

  // -------------------------
  // Clear filters
  // -------------------------
  const clearFilters = () => {
    setPOrgUnit(null);
    setPRecruiter(null);
    setPCategory(null);
    setPSubCat(null);
    setPGroup(null);
    setPEmpStatus(null);
    setPDocStatus(null);
    setPDocTypes([]);
    setPSearch("");

    setFilters({});
    setRows([]);
    setHasSearched(false);
    setCurrentPage(1);
  };

  // -------------------------
  // Row click handler
  // -------------------------
  const handleRowClick = async (row) => {
    setSelectedUser(row);
    setShowModal(true);
    setModalLoading(true);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${BASE_URL}/api/reports/document-status-details`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            userId: row.user_id,
          },
        }
      );

      const order = {
        completed: 1,
        almost_expired: 2,
        review: 3,
        not_completed: 4,
      };

      const sorted = (res.data || []).sort((a, b) => {
        return (order[a.status] || 99) - (order[b.status] || 99);
      });

      setTaskDetails(sorted);
    } catch (err) {
      console.error("Failed to load document details", err);
      setTaskDetails([]);
    } finally {
      setModalLoading(false);
    }
  };

  // -------------------------
  // Pagination
  // -------------------------
  const paginated = rows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // -------------------------
  // Export CSV
  // -------------------------
  const exportCSV = () => {
    if (!hasSearched || rows.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Org Unit",
      "First Name",
      "Last Name",
      "Not Started",
      "Ready for Review",
      "Almost Expired",
      "Complete",
    ];

    const lines = rows.map((r) =>
      [
        r.org_unit,
        r.first_name,
        r.last_name,
        r.not_started,
        r.ready_for_review,
        r.almost_expired,
        r.complete,
      ]
        .map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );

    const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "DocumentStatusReport.csv";
    link.click();
  };

  // -------------------------
  // Render
  // -------------------------
  return (
    <Container className="form-wrapper my-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="report-page-title fw-bold m-0"><span className="report-title-icon" aria-hidden="true"><i className="fas fa-file-alt" /></span><span>Document Status</span></h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          ⬅ Return to Report List
        </Button>
      </div>

      {/* Row 1 */}
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col>
          <label>Org Unit</label>
          <Select
            options={orgUnits.map((o) => ({
              value: o.ou_id,
              label: o.ou_name,
            }))}
            value={pOrgUnit}
            onChange={setPOrgUnit}
          />
        </Col>

        <Col>
          <label>Recruiter</label>
          <Select
            options={recruiters}
            value={pRecruiter}
            onChange={setPRecruiter}
          />
        </Col>

        <Col>
          <label>Category</label>
          <Select
            options={categories.map((c) => ({
              value: c.c_id,
              label: c.c_name,
            }))}
            value={pCategory}
            onChange={setPCategory}
          />
        </Col>

        <Col>
          <label>Sub Category</label>
          <Select
            options={subCats.map((sc) => ({
              value: sc.sc_id,
              label: sc.sc_name,
            }))}
            value={pSubCat}
            onChange={setPSubCat}
          />
        </Col>
      </Row>

      {/* Row 2 */}
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col>
          <label>Group</label>
          <Select options={groups} value={pGroup} onChange={setPGroup} />
        </Col>

        <Col>
          <label>Employment Status</label>
          <Select
            options={statuses}
            value={pEmpStatus}
            onChange={setPEmpStatus}
          />
        </Col>

        <Col>
          <label>Document Types</label>
          <Select
            isMulti
            options={docTypes}
            value={pDocTypes}
            onChange={setPDocTypes}
          />
        </Col>

        <Col>
          <label>Document Status</label>
          <Select
            options={DOC_STATUS_OPTIONS}
            value={pDocStatus}
            onChange={setPDocStatus}
          />
        </Col>
      </Row>

      {/* Row 3 */}
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={3}>
          <label>Search Name</label>
          <Form.Control
            placeholder="Search first or last name"
            value={pSearch}
            onChange={(e) => setPSearch(e.target.value)}
          />
        </Col>
      </Row>

      {/* Buttons */}
      <Row className="mb-3 g-3 align-items-end report-row report-actions-row">
        <Col md="auto">
          <div className="button-group">
            <Button variant="primary" onClick={applyFilters}>
              <i className="fas fa-filter" aria-hidden="true" /> Filter
            </Button>
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filter
            </Button>
          </div>
        </Col>

        <Col md="auto" className="ms-auto">
          <Button
            className="btn-export"
            onClick={exportCSV}
            disabled={!hasSearched || rows.length === 0}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {/* Table */}
      {!hasSearched ? (
        <div className="text-center text-muted py-5">
          Use the filters above and click <strong>Filter</strong> to load
          results.
        </div>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table bordered hover size="sm" className="compact-table">
            <thead className="table-dark text-center">
              <tr>
                <th>Org Unit</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Not Started</th>
                <th>Ready for Review</th>
                <th>Almost Expired</th>
                <th>Complete</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    No results found with current filters.
                  </td>
                </tr>
              ) : (
                paginated.map((r, i) => (
                  <tr
                    key={i}
                    className="clickable-row"
                    onClick={() => handleRowClick(r)}
                  >
                    <td className="text-center">{r.org_unit}</td>
                    <td className="text-center">{r.first_name}</td>
                    <td className="text-center">{r.last_name}</td>
                    <td className="text-center">{r.not_started}</td>
                    <td className="text-center">{r.ready_for_review}</td>
                    <td className="text-center">{r.almost_expired}</td>
                    <td className="text-center">{r.complete}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Pagination
            currentPage={currentPage}
            totalItems={rows.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* -------------------------
        Document Status Modal
        ------------------------- */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Document Status Details
            {selectedUser && (
              <div className="text-muted fs-6">
                {selectedUser.first_name} {selectedUser.last_name} (
                {selectedUser.userid_no}) — {selectedUser.org_unit}
              </div>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {modalLoading ? (
            <div className="text-center my-4">
              <Spinner animation="border" />
            </div>
          ) : taskDetails.length === 0 ? (
            <div className="text-center text-muted">
              No document tasks found.
            </div>
          ) : (
            <Table bordered size="sm">
              <thead className="table-light">
                <tr>
                  <th>Task Name</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {taskDetails.map((t, i) => (
                  <tr key={i}>
                    <td>{t.task_name}</td>
                    <td className="text-center">
                      {t.status === "completed" && <span className="report-status completed"><i className="fas fa-check-circle" aria-hidden="true" />Complete</span>}
                      {t.status === "almost_expired" && <span className="report-status almost-expired"><i className="fas fa-clock" aria-hidden="true" />Almost Expired</span>}
                      {t.status === "review" && <span className="report-status review"><i className="fas fa-exclamation-circle" aria-hidden="true" />Review</span>}
                      {t.status === "not_completed" && <span className="report-status not-started"><i className="fas fa-minus-circle" aria-hidden="true" />Not Started</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
